import { Typography, Table, Tag, Collapse, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface Endpoint {
  method: string;
  path: string;
  description: string;
  auth: string;
}

const methodColor: Record<string, string> = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  DELETE: 'red',
};

const columns = [
  {
    title: 'Method',
    dataIndex: 'method',
    width: 80,
    render: (m: string) => <Tag color={methodColor[m] || 'default'}>{m}</Tag>,
  },
  {
    title: 'Endpoint',
    dataIndex: 'path',
    render: (p: string) => <Text code copyable={{ text: p }}>{p}</Text>,
  },
  {
    title: 'Description',
    dataIndex: 'description',
  },
];

const userEndpoints: Endpoint[] = [
  { method: 'GET', path: '/api/v1/users', description: 'List all users', auth: 'admin' },
  { method: 'POST', path: '/api/v1/users', description: 'Create user. Body: username, password, role', auth: 'admin' },
  { method: 'PUT', path: '/api/v1/users/:id', description: 'Update user role/active status', auth: 'admin' },
  { method: 'DELETE', path: '/api/v1/users/:id', description: 'Deactivate user (soft delete)', auth: 'admin' },
  { method: 'PUT', path: '/api/v1/users/:id/password', description: 'Admin reset user password', auth: 'admin' },
];

const apiKeyEndpoints: Endpoint[] = [
  { method: 'GET', path: '/api/v1/api-keys', description: 'List all API keys', auth: 'admin' },
  { method: 'POST', path: '/api/v1/api-keys', description: 'Create API key. Body: name, role, expires_at (optional)', auth: 'admin' },
  { method: 'DELETE', path: '/api/v1/api-keys/:id', description: 'Revoke API key', auth: 'admin' },
];

const providerEndpoints: Endpoint[] = [
  { method: 'GET', path: '/api/v1/providers/status', description: 'Status of all configured providers', auth: 'admin' },
  { method: 'GET', path: '/api/v1/providers/icecat', description: 'IceCat provider settings', auth: 'admin' },
  { method: 'PUT', path: '/api/v1/providers/icecat', description: 'Update IceCat credentials', auth: 'admin' },
  { method: 'POST', path: '/api/v1/providers/icecat/test', description: 'Test IceCat API connection', auth: 'admin' },
  { method: 'GET', path: '/api/v1/providers/icecat/cache', description: 'IceCat cache statistics', auth: 'admin' },
  { method: 'DELETE', path: '/api/v1/providers/icecat/cache', description: 'Clear all IceCat cached data', auth: 'admin' },
  { method: 'POST', path: '/api/v1/providers/icecat/refresh/:product_id', description: 'Force refresh product from IceCat', auth: 'admin' },
  { method: 'GET', path: '/api/v1/providers/icecat/cache/:product_id/raw', description: 'Raw cached IceCat API response (debug)', auth: 'admin' },
  { method: 'POST', path: '/api/v1/providers/icecat/search', description: 'Search IceCat directly. Body: mpn, brand, or gtin', auth: 'admin' },
  { method: 'POST', path: '/api/v1/providers/icecat/preseed', description: 'Start pre-seed job for a brand. Body: brand', auth: 'admin' },
  { method: 'GET', path: '/api/v1/providers/icecat/preseed/jobs', description: 'Pre-seed job history', auth: 'admin' },
  { method: 'POST', path: '/api/v1/providers/icecat/backfill', description: 'Start backfill job. Body: source_provider', auth: 'admin' },
  { method: 'GET', path: '/api/v1/providers/icecat/backfill/jobs', description: 'Backfill job history', auth: 'admin' },
  { method: 'POST', path: '/api/v1/providers/icecat/queue/process', description: 'Process IceCat lookup queue (batch: 100)', auth: 'admin' },
  { method: 'GET', path: '/api/v1/providers/icecat/queue/stats', description: 'IceCat lookup queue statistics', auth: 'admin' },
];

const importEndpoints: Endpoint[] = [
  { method: 'GET', path: '/api/v1/etl/install', description: 'Check if Etilize importer is installed', auth: 'admin' },
  { method: 'POST', path: '/api/v1/etl/seed', description: 'Download and install Etilize importer', auth: 'admin' },
  { method: 'POST', path: '/api/v1/etl/upgrade', description: 'Re-download Etilize importer', auth: 'admin' },
  { method: 'POST', path: '/api/v1/update', description: 'Start Etilize ETL update', auth: 'admin' },
  { method: 'GET', path: '/api/v1/etl/status', description: 'ETL process status', auth: 'admin' },
  { method: 'GET', path: '/api/v1/import/formats', description: 'Available CSV import formats', auth: 'admin' },
  { method: 'POST', path: '/api/v1/import/csv/upload', description: 'Import from uploaded CSV. Form: file, format, source_name', auth: 'admin' },
  { method: 'GET', path: '/api/v1/import/status/:job_id', description: 'Import job status', auth: 'admin' },
  { method: 'GET', path: '/api/v1/import/jobs', description: 'Recent import jobs. Param: limit', auth: 'admin' },
];

const aliasEndpoints: Endpoint[] = [
  { method: 'GET', path: '/api/v1/aliases/manufacturers', description: 'List manufacturer alias mappings', auth: 'admin' },
  { method: 'POST', path: '/api/v1/aliases/manufacturers', description: 'Add manufacturer alias. Body: alias_name, canonical_name', auth: 'admin' },
  { method: 'DELETE', path: '/api/v1/aliases/manufacturers/:id', description: 'Delete manufacturer alias', auth: 'admin' },
  { method: 'GET', path: '/api/v1/aliases/categories', description: 'List category alias mappings', auth: 'admin' },
  { method: 'POST', path: '/api/v1/aliases/categories', description: 'Add category alias. Body: alias_name, canonical_name', auth: 'admin' },
  { method: 'DELETE', path: '/api/v1/aliases/categories/:id', description: 'Delete category alias', auth: 'admin' },
];

const logEndpoints: Endpoint[] = [
  { method: 'GET', path: '/api/v1/logs', description: 'Query API logs. Params: client, endpoint, days, limit, offset', auth: 'admin' },
  { method: 'GET', path: '/api/v1/logs/summary', description: 'API usage summary. Param: days', auth: 'admin' },
];

const settingsEndpoints: Endpoint[] = [
  { method: 'GET', path: '/api/v1/settings', description: 'Current application settings', auth: 'admin' },
  { method: 'PUT', path: '/api/v1/settings/smtp', description: 'Update SMTP settings', auth: 'admin' },
  { method: 'GET', path: '/api/v1/settings/etl', description: 'ETL sync settings', auth: 'admin' },
  { method: 'PUT', path: '/api/v1/settings/etl', description: 'Update ETL settings', auth: 'admin' },
];

const sections = [
  { key: 'users', label: 'User Management', data: userEndpoints },
  { key: 'apikeys', label: 'API Keys', data: apiKeyEndpoints },
  { key: 'providers', label: 'Providers (IceCat)', data: providerEndpoints },
  { key: 'import', label: 'Import & ETL', data: importEndpoints },
  { key: 'aliases', label: 'Aliases', data: aliasEndpoints },
  { key: 'logs', label: 'API Logs', data: logEndpoints },
  { key: 'settings', label: 'Settings', data: settingsEndpoints },
];

export function ApiGuideAdminPage() {
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Title level={3} style={{ margin: 0 }}>Admin API Reference</Title>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          href="/FN-PIM_API_Documentation.md"
          download="FN-PIM_API_Documentation.md"
        >
          Download Full Docs
        </Button>
      </div>

      <Collapse
        defaultActiveKey={['users', 'apikeys']}
        items={sections.map((s) => ({
          key: s.key,
          label: <Text strong>{s.label} ({s.data.length})</Text>,
          children: (
            <Table
              dataSource={s.data.map((e, i) => ({ ...e, _key: i }))}
              rowKey="_key"
              columns={columns}
              size="small"
              pagination={false}
            />
          ),
        }))}
      />
    </div>
  );
}
